import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { LibraryResource } from 'types';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  LibraryResourceListRequest,
  LibraryResourceListResponse,
  LibraryResourceDetailResponse,
} from './types';

const tagType = 'LIBRARY_RESOURCES' as const;

const buildQueryString = (
  tenantId: string,
  params?: Omit<LibraryResourceListRequest, 'tenantId'>
) => {
  if (!params) return generatePath(URL.LIBRARY_RESOURCES, { tenantId });

  const searchParams = new URLSearchParams();

  Object.entries(
    params as Record<string, string | number | string[] | undefined>
  ).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      value.forEach((item) => {
        searchParams.append(key, item);
      });
      return;
    }

    if (typeof value === 'string' && value.length === 0) return;

    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  const baseUrl = generatePath(URL.LIBRARY_RESOURCES, { tenantId });
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const libraryResourcesApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'libraryResourcesApi',
  endpoints: (build) => ({
    import: build.mutation<void, { data: FormData; tenantId: string }>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ data, tenantId }) => ({
        data,
        method: 'post',
        url: generatePath(URL.LIBRARY_RESOURCE_IMPORT, { tenantId }),
      }),
    }),
    delete: build.mutation<
      { success: boolean },
      { id: string; tenantId: string }
    >({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id, tenantId }) => ({
        method: 'delete',
        url: generatePath(URL.LIBRARY_RESOURCE_DETAILS, { id, tenantId }),
      }),
    }),
    details: build.query<LibraryResource, { id: string; tenantId: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: LibraryResourceDetailResponse) =>
        response.data,
      query: ({ id, tenantId }) => ({
        method: 'get',
        url: generatePath(URL.LIBRARY_RESOURCE_DETAILS, { id, tenantId }),
      }),
    }),
    create: build.mutation<
      LibraryResource,
      { data: FormData; tenantId: string }
    >({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: LibraryResourceDetailResponse) =>
        response.data,
      query: ({ data, tenantId }) => ({
        data,
        method: 'post',
        headers: { 'Content-Type': 'multipart/form-data' },
        url: generatePath(URL.LIBRARY_RESOURCES, { tenantId }),
      }),
    }),
    update: build.mutation<
      LibraryResource,
      { id: string; data: FormData; tenantId: string }
    >({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: LibraryResourceDetailResponse) =>
        response.data,
      query: ({ id, data, tenantId }) => ({
        data,
        method: 'put',
        headers: { 'Content-Type': 'multipart/form-data' },
        url: generatePath(URL.LIBRARY_RESOURCE_DETAILS, { id, tenantId }),
      }),
    }),
    list: build.query<
      LibraryResourceListResponse['data'],
      LibraryResourceListRequest | undefined
    >({
      providesTags: providesList(tagType),
      transformResponse: (response: LibraryResourceListResponse) =>
        response.data,
      query: (params) => {
        if (!params) {
          throw new Error('Tenant ID is required to fetch resources.');
        }

        const { tenantId, ...queryParams } = params;
        return {
          method: 'get',
          url: buildQueryString(tenantId, queryParams),
        };
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useImportMutation,
  useLazyDetailsQuery,
} = libraryResourcesApi;

export type { LibraryResourceListRequest };
